import { useState } from 'react';

import cx from 'classnames';
import { always } from 'ramda';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { FormBuilder } from 'components/Layouts/Form/FormBuilder';
import { ProposalSettingsMenu } from 'components/Layouts/Form/Header/ProposalSettingsMenu';
import { getItineraryNavLinks } from 'components/Pages/Admin/Page/Landing/utils';
import BuilderPageHeader from 'components/shared/LandingPage/blocks/PageHeader';
import { LANDING_PAGES_CONTAINER_ID } from 'components/shared/LandingPage/components/Grid';
import { MaybeVirtualizer } from 'components/shared/LandingPage/Virtualizer';
import { ITINERARIES_ITEM_TYPES } from 'constants/index';
import { useCustomFonts, useCustomStyles } from 'hooks/useCustomFront';
import { useCustomStylesWatcher } from 'hooks/useCustomStylesWatcher';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useWebViewLinksHandler } from 'hooks/useWebViewLinksHandler';
import { itinerariesItemDataSelector, itineraryWithoutEmptyPagesSelector } from 'store/itinerariesItem/selectors';
import { saveAsDOCX } from 'utils/filesDownloaders/docx';
import { saveAsPDF } from 'utils/filesDownloaders/pdf';
import { getLandingTheme, isSample, mapPathSegment2ItemType } from 'utils/itineraries/functions';
import {
  removeAIBlocksFromProposal,
  useItinerariesItemSaveHandler,
  useSaveSampleItemSaveHandler,
  useSaveSampleLookbookSaveHandler,
} from 'utils/itineraries/functions/builderUtils';
import { ILanding, IPage, IProposal } from 'utils/itineraryDetails/types';

import ItinerariesItemActionButtons from '../../ItinerariesItemBuilder/ActionButtons';
import BuilderPagePreview from '../BuilderPagePreview';
import { useProposalDifferenceHandler } from '../functions';
import Builder from './Builder';
import useInlineEditor, { getDefaultActiveAnchor, useContentPagePreview } from './functions';
import styles from './index.module.css';
import { IItinerariesItemLandingProps, IRenderBuilderPageHeaderProps, IRouteParams } from './types';

function ItinerariesItemLanding({ canSaveAsSample }: IItinerariesItemLandingProps) {
  const [submitting, setSubmitting] = useState(false);

  const itineraryItem = useSelector(itineraryWithoutEmptyPagesSelector);
  const landingTheme = getLandingTheme(itineraryItem);

  useInlineEditor(itineraryItem);
  useCustomStyles({ css: itineraryItem.customStyles, customId: itineraryItem.id });
  useCustomFonts({ landingItem: itineraryItem, customId: itineraryItem.id });
  useCustomStylesWatcher({ pageId: itineraryItem.id, isItinerariesItem: true });
  useWebViewLinksHandler();
  const { itemType: itemTypePathSegment } = useParams<{ itemType: string }>();
  const itemType = mapPathSegment2ItemType(itemTypePathSegment);

  const { isTabletOrMobile } = useMediaBreakpoints();

  const pages = itineraryItem?.landing?.pages || [];
  const navLinks = getItineraryNavLinks(pages);

  const { updatedPages, setNewPages } = useProposalDifferenceHandler(pages);

  const handleSaveWithoutSnackbar = useItinerariesItemSaveHandler({
    onPrepareSaveData: (itinerary: ILanding) => {
      return removeAIBlocksFromProposal(itinerary);
    },
    showSuccessSnackbar: false,
    onAfterSave: (newItinerary: IProposal) => {
      // FIXME: fix type assertion
      setNewPages(newItinerary.landing.pages as never[]);
    },
  });

  const handleSave = useItinerariesItemSaveHandler({
    onPrepareSaveData: (itinerary: ILanding) => {
      return removeAIBlocksFromProposal(itinerary);
    },
    onAfterSave: (newItinerary: IProposal) => {
      // FIXME: fix type assertion
      setNewPages(newItinerary.landing.pages as never[]);
    },
  });

  const saveAsSample = useSaveSampleItemSaveHandler();
  const saveAsLookbookSample = useSaveSampleLookbookSaveHandler();
  const { withContentPagePreview, pagePreviewId } = useContentPagePreview();
  const defaultActiveAnchor = getDefaultActiveAnchor(pages, navLinks, pagePreviewId as number);

  const onSave = async () => {
    try {
      setSubmitting(true);
      await handleSave({
        pages: updatedPages,
        customStyles: itineraryItem?.customStyles,
        customFonts: itineraryItem?.customFonts,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAsSample = async () => {
    try {
      setSubmitting(true);
      await handleSaveWithoutSnackbar({ pages: updatedPages });
      if (itemType === ITINERARIES_ITEM_TYPES.LOOKBOOK) {
        await saveAsLookbookSample(itineraryItem?.id);
      } else {
        await saveAsSample(itineraryItem?.id);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const landing = useSelector(itinerariesItemDataSelector);

  if (!pages && pages?.length < 1) return null;

  const renderBuilderPageHeader = ({ links, pageIndex }: IRenderBuilderPageHeaderProps) => {
    if (pageIndex !== 0) return null;

    return <BuilderPageHeader links={links} disabled />;
  };

  return (
    // @ts-ignore // TODO: fix props with FormBuilder refactoring (js to ts)
    <FormBuilder
      withNav={true}
      navLinks={navLinks}
      isItinerariesItem
      className={cx(styles.formBuilder, `custom-main-${landingTheme}`)}
      sectionClassName={`custom-section-${landingTheme}`}
      landingTheme={landingTheme}
      defaultActiveAnchor={defaultActiveAnchor}
    >
      <MaybeVirtualizer
        getEnabled={() => !isTabletOrMobile && landingTheme === 'default'}
        count={pages.length}
        getContentRef={() => ({ current: document.getElementById(LANDING_PAGES_CONTAINER_ID) as HTMLDivElement })}
        getRerenderDeps={({ visibleItems }) => [!visibleItems || visibleItems.length === 0 ? null : landing]}
      >
        {({ getPageVirtualized } = {}) => (
          <>
            {pages?.map((page: IPage, i: number) => (
              <Builder
                renderPageHeader={renderBuilderPageHeader}
                key={i}
                landingData={page}
                anchor={navLinks[i]}
                screens={navLinks}
                pageIndex={i}
                page={itineraryItem}
                landingTheme={landingTheme}
                virtualized={getPageVirtualized?.(i)}
              />
            ))}
          </>
        )}
      </MaybeVirtualizer>
      <ItinerariesItemActionButtons
        onSave={onSave}
        submitting={submitting}
        onPDF={() =>
          onSave().then(() =>
            saveAsPDF({
              item: itineraryItem,
              itinerariesItemType: itineraryItem.type,
            } as any),
          )
        }
        onDOCX={(themeId: string, etag: string) =>
          onSave().then(() =>
            saveAsDOCX({ item: itineraryItem, itinerariesItemType: itineraryItem.type, themeId, etag }),
          )
        }
        onSaveAsSample={canSaveAsSample ? handleSaveAsSample : undefined}
      />
      <ProposalSettingsMenu submitting={submitting} />
      {withContentPagePreview && <BuilderPagePreview pagePreviewId={pagePreviewId} />}
    </FormBuilder>
  );
}

export default ItinerariesItemLanding;
